function getOptionalProductNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatOptionalProductMoney(cents, moneyFormat) {
  const format = moneyFormat || '${{ amount }}';
  const placeholder = format.match(/{{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*}}/);
  if (!placeholder) return (cents / 100).toFixed(2);

  const formatType = placeholder[1];
  const useDecimals = !formatType.includes('no_decimals');
  const useCommaSeparator = formatType.includes('comma_separator');
  const amount = (cents / 100).toLocaleString(useCommaSeparator ? 'de-DE' : 'en-GB', {
    minimumFractionDigits: useDecimals ? 2 : 0,
    maximumFractionDigits: useDecimals ? 2 : 0,
  });

  return format.replace(placeholder[0], amount);
}

function updateOptionalProductPrice(element, totalPrice, totalComparePrice) {
  const priceRoot = document.getElementById(`price-${element.dataset.sectionId}`);
  if (!priceRoot) return;

  const price = priceRoot.querySelector('.price');
  if (!price) return;

  const salePrice = price.querySelector('.price__sale .price-item--sale');
  const regularPrice = price.querySelector('.price__regular .price-item--regular');
  const comparePrice = price.querySelector('.price__compare-price .price-item--regular');
  const formattedPrice = formatOptionalProductMoney(totalPrice, element.dataset.moneyFormat);

  if (price.classList.contains('price--on-sale') && salePrice) {
    salePrice.textContent = formattedPrice;
    if (comparePrice) comparePrice.textContent = formatOptionalProductMoney(totalComparePrice, element.dataset.moneyFormat);
  } else if (regularPrice) {
    regularPrice.textContent = formattedPrice;
  }

  const vatPrice = priceRoot.querySelector('.product-price-vat');
  if (vatPrice) vatPrice.textContent = `${formatOptionalProductMoney(Math.round(totalPrice * 5 / 6), element.dataset.moneyFormat)} EXCL VAT`;
}

function getOptionalProductProperties(formData) {
  const properties = {};

  for (const [key, value] of formData.entries()) {
    if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
    const propertyName = key.slice(11, -1);
    if (propertyName && value !== '') properties[propertyName] = value;
  }

  return properties;
}

class AdditionalProducts extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.basePrice = getOptionalProductNumber(this.dataset.basePrice);
    this.baseComparePrice = getOptionalProductNumber(this.dataset.baseComparePrice);
    this.addEventListener('change', this.handleChange);
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    this.updateDisplay();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.variantChangeUnsubscriber?.();
  }

  handleChange(event) {
    if (!event.target.matches('[data-additional-product-input]')) return;

    const input = event.target;
    const group = input.closest('[data-additional-products-group]');
    const noneInput = group?.querySelector('[data-additional-product-none]');
    if (input.hasAttribute('data-additional-product-none') && input.checked) {
      group?.querySelectorAll('[data-additional-product-input][data-variant-id]:checked').forEach((productInput) => {
        productInput.checked = false;
      });
    }

    if (input.dataset.variantId && input.checked && noneInput) noneInput.checked = false;

    if (input.dataset.variantId && !input.checked && noneInput) {
      const hasSelectedProduct = group?.querySelector('[data-additional-product-input][data-variant-id]:checked');
      if (!hasSelectedProduct) noneInput.checked = true;
    }

    this.updateDisplay();
    document.querySelector(`suggested-add-ons#SuggestedAddOns-${this.dataset.sectionId}`)?.updateDisplay?.();
  }

  handleVariantChange(event) {
    if (event?.data?.sectionId !== this.dataset.sectionId || !event.data.variant) return;
    this.updateBaseVariant(event.data.variant);
  }

  updateBaseVariant(variant) {
    this.basePrice = getOptionalProductNumber(variant.price);
    this.baseComparePrice = getOptionalProductNumber(variant.compare_at_price);
    this.updateDisplay();
  }

  getSelectedProducts() {
    return Array.from(this.querySelectorAll('[data-additional-product-input][data-variant-id]:checked')).map((input) => ({
      variantId: input.dataset.variantId,
      title: input.dataset.title || '',
      price: getOptionalProductNumber(input.dataset.price),
      comparePrice: getOptionalProductNumber(input.dataset.comparePrice),
    }));
  }

  getCartItems(formData) {
    const selectedProducts = this.getSelectedProducts();
    if (!selectedProducts.length) return null;

    const quantity = Math.max(1, getOptionalProductNumber(formData.get('quantity')) || 1);
    const items = [{ id: formData.get('id'), quantity }];
    const properties = getOptionalProductProperties(formData);
    if (Object.keys(properties).length) items[0].properties = properties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) items[0].selling_plan = sellingPlan;

    selectedProducts.forEach((product) => {
      items.push({
        id: product.variantId,
        quantity,
        properties: {
          _additional_product_title: product.title,
          _additional_product_parent: this.dataset.parentTitle || '',
        },
      });
    });

    return items;
  }

  updateDisplay() {
    this.querySelectorAll('[data-additional-products-group]').forEach((group) => {
      const selectedLabel = group.querySelector('[data-selected-additional-products-title]');
      if (!selectedLabel) return;

      const selectedTitles = Array.from(group.querySelectorAll('[data-additional-product-input][data-variant-id]:checked'))
        .map((input) => input.dataset.title || '')
        .filter(Boolean);
      selectedLabel.textContent = selectedTitles.length ? selectedTitles.join(', ') : group.dataset.noneTitle || 'None';
    });

    const suggestedAddOns = document.querySelector(`suggested-add-ons#SuggestedAddOns-${this.dataset.sectionId}`);
    if (suggestedAddOns?.updateDisplay) {
      suggestedAddOns.updateDisplay();
      return;
    }

    const selectedProducts = this.getSelectedProducts();
    const productTotal = selectedProducts.reduce((total, product) => total + product.price, 0);
    const productCompareTotal = selectedProducts.reduce((total, product) => total + product.comparePrice, 0);
    updateOptionalProductPrice(
      this,
      this.basePrice + productTotal,
      this.baseComparePrice + productCompareTotal
    );
  }
}

class SuggestedAddOns extends HTMLElement {
  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleShowMore = this.handleShowMore.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.basePrice = getOptionalProductNumber(this.dataset.basePrice);
    this.baseComparePrice = getOptionalProductNumber(this.dataset.baseComparePrice);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleShowMore);
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
    this.updateDisplay();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('click', this.handleShowMore);
    this.variantChangeUnsubscriber?.();
  }

  handleShowMore(event) {
    const button = event.target.closest('[data-add-on-show-more]');
    if (!button || !this.contains(button)) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    this.querySelectorAll('[data-add-on-option]').forEach((option, index) => {
      option.hidden = isExpanded && index > 0;
    });
    this.querySelectorAll('[data-add-on-group]').forEach((group, index) => {
      group.hidden = isExpanded && index > 0;
    });

    button.setAttribute('aria-expanded', String(!isExpanded));
    const label = button.querySelector('[data-add-on-show-more-label]');
    if (label) label.textContent = isExpanded ? button.dataset.showMoreLabel : button.dataset.showLessLabel;

    if (isExpanded) {
      const headerHeightVariable = window.getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height');
      const headerElements = [
        document.querySelector('.section-header'),
        document.querySelector('[data-gs-header]'),
        document.querySelector('[data-gs-header] .gs-header-main'),
        document.querySelector('[data-gs-header] .gs-mobile-search'),
      ].filter(Boolean);
      const headerHeight = headerElements.reduce((height, element) => {
        return Math.max(height, element.getBoundingClientRect().bottom);
      }, Number.parseFloat(headerHeightVariable) || 0);

      const anchorTop = this.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: Math.max(0, anchorTop - headerHeight - 16),
        behavior: 'smooth',
      });
    }
  }

  handleChange(event) {
    if (!event.target.matches('[data-add-on-input]')) return;

    const input = event.target;
    const group = input.closest('[data-add-on-group]');
    const noneInput = group?.querySelector('[data-add-on-input][value=""]');

    if (input.dataset.variantId && input.checked && noneInput) noneInput.checked = false;

    if (!input.dataset.variantId && input.checked) {
      group?.querySelectorAll('[data-add-on-input][data-variant-id]:checked').forEach((addOnInput) => {
        addOnInput.checked = false;
      });
    }

    if (input.dataset.variantId && !input.checked && noneInput) {
      const hasSelectedAddOn = group?.querySelector('[data-add-on-input][data-variant-id]:checked');
      if (!hasSelectedAddOn) noneInput.checked = true;
    }

    this.updateDisplay();
  }

  handleVariantChange(event) {
    if (event?.data?.sectionId !== this.dataset.sectionId || !event.data.variant) return;
    this.updateBaseVariant(event.data.variant);
  }

  updateBaseVariant(variant) {
    this.basePrice = getOptionalProductNumber(variant.price);
    this.baseComparePrice = getOptionalProductNumber(variant.compare_at_price);
    this.updateDisplay();
  }

  getSelectedAddOns() {
    return Array.from(this.querySelectorAll('[data-add-on-input]:checked'))
      .filter((input) => input.dataset.variantId)
      .map((input) => ({
        variantId: input.dataset.variantId,
        groupTitle: input.dataset.groupTitle || '',
        title: input.dataset.title || '',
        price: getOptionalProductNumber(input.dataset.price),
        comparePrice: getOptionalProductNumber(input.dataset.comparePrice),
      }));
  }

  getAdditionalProductsElement() {
    return document.querySelector(`additional-products#AdditionalProducts-${this.dataset.sectionId}`);
  }

  getCartItems(formData) {
    const selectedAddOns = this.getSelectedAddOns();
    const selectedProducts = this.getAdditionalProductsElement()?.getSelectedProducts() || [];
    if (!selectedAddOns.length && !selectedProducts.length) return null;

    const quantity = Math.max(1, getOptionalProductNumber(formData.get('quantity')) || 1);
    const items = [{ id: formData.get('id'), quantity }];
    const properties = getOptionalProductProperties(formData);
    if (Object.keys(properties).length) items[0].properties = properties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) items[0].selling_plan = sellingPlan;

    selectedAddOns.forEach((addOn) => {
      items.push({
        id: addOn.variantId,
        quantity,
        properties: {
          _suggested_add_on_group: addOn.groupTitle,
          _suggested_add_on_parent: this.dataset.parentTitle || '',
        },
      });
    });

    selectedProducts.forEach((product) => {
      items.push({
        id: product.variantId,
        quantity,
        properties: {
          _additional_product_title: product.title,
          _additional_product_parent: this.dataset.parentTitle || '',
        },
      });
    });

    return items;
  }

  updateDisplay() {
    this.querySelectorAll('[data-add-on-group]').forEach((group) => {
      const selectedLabel = group.querySelector('[data-selected-add-on-title]');
      if (!selectedLabel) return;

      const selectedTitles = Array.from(group.querySelectorAll('[data-add-on-input][data-variant-id]:checked'))
        .map((input) => input.dataset.title || '')
        .filter(Boolean);
      const noneInput = group.querySelector('[data-add-on-input][value=""]');
      const noneTitle = noneInput?.dataset.title || '';
      selectedLabel.textContent = selectedTitles.length ? selectedTitles.join(', ') : noneTitle;
    });

    const selectedAddOns = this.getSelectedAddOns();
    const selectedProducts = this.getAdditionalProductsElement()?.getSelectedProducts() || [];
    const addOnTotal = selectedAddOns.reduce((total, addOn) => total + addOn.price, 0);
    const addOnCompareTotal = selectedAddOns.reduce((total, addOn) => total + addOn.comparePrice, 0);
    const productTotal = selectedProducts.reduce((total, product) => total + product.price, 0);
    const productCompareTotal = selectedProducts.reduce((total, product) => total + product.comparePrice, 0);

    updateOptionalProductPrice(
      this,
      this.basePrice + addOnTotal + productTotal,
      this.baseComparePrice + addOnCompareTotal + productCompareTotal
    );
  }
}

if (!customElements.get('additional-products')) {
  customElements.define('additional-products', AdditionalProducts);
}

if (!customElements.get('suggested-add-ons')) {
  customElements.define('suggested-add-ons', SuggestedAddOns);
}
